<script setup lang="ts">
import {Button} from "@/Components/Library";
import LaunchHeader from "@/Pages/User/Questions/Launch/LaunchHeader.vue";
import MassOperationProgressModal from "@/Components/MassOperationProgressModal.vue";
import {useMainStore} from "@/Stores/store";
import {deleteMultipleQuestions, duplicateMultipleQuestions} from "@/Services/questions";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {ref, computed} from "vue"; // ...changed: added computed
import useEcho from "@/Composables/echo";
import {Question} from "@/Types/question";

const toast = useToast();
const confirm = useConfirm();
const $store = useMainStore();

const {questions, state, params} = defineProps<{
    questions: Question[];
    state: {
        show: {
            shareDialog: boolean;
        };
    };
    params: {
        filter: {
            state: "created" | "archived";
        };
        sort: string;
    };
}>();
const emit = defineEmits<{
    (e: "cancel"): void;
    (e: "removeQuestions", questionIds: Array<string>): void;
    (e: "startSharing", questionData: Array<{id: string; title: string}>): void;
}>();

const loading = ref(false);

// ...existing code...
// removed: showDuplicateModal, showShareModal
const duplicatingQuestions = ref<Array<{id: string; title: string; status: "pending" | "completed" | "error"}>>([]);
const sharingQuestions = ref<Array<{id: string; title: string; status: "pending" | "completed" | "error"}>>([]);

// new refs to control single modal
const modalVisible = ref(false);
const modalOperationType = ref<"duplicate" | "share">("duplicate");
const modalQuestions = computed(() => {
    if (modalOperationType.value === "duplicate") {
        return duplicatingQuestions.value;
    }
    if (modalOperationType.value === "share") {
        return sharingQuestions.value;
    }
    return [];
});

useEcho([
    {
        event: "Question\\QuestionDuplicated",
        callback: (e: {question: string}) => {
            const index = duplicatingQuestions.value.findIndex((q) => q.id === e.question);
            console.log("MASS DUPLICATE", e, index, duplicatingQuestions.value);
            if (index !== -1) {
                duplicatingQuestions.value[index].status = "completed";
            }
        },
    },
    {
        event: "Question\\QuestionDuplicatedError",
        callback: (e: {question: string}) => {
            const index = duplicatingQuestions.value.findIndex((q) => q.id === e.question);
            console.log("DUPLICATE ERR", e, index, duplicatingQuestions.value);
            if (index !== -1) {
                duplicatingQuestions.value[index].status = "error";
            }
        },
    },
    {
        event: "Question\\QuestionShared",
        callback: (e: {question: string}) => {
            const index = sharingQuestions.value.findIndex((q) => q.id === e.question);
            console.log("MASS SHARE", e, index, sharingQuestions.value);
            if (index !== -1) {
                sharingQuestions.value[index].status = "completed";
            }
        },
    },
    {
        event: "Question\\QuestionSharedError",
        callback: (e: {question: string}) => {
            const index = sharingQuestions.value.findIndex((q) => q.id === e.question);
            console.log("SHARE ERR", e, index, sharingQuestions.value);
            if (index !== -1) {
                sharingQuestions.value[index].status = "error";
            }
        },
    },
]);

const handleDuplicateMultiple = async () => {
    confirm.require({
        header: "Duplicate posts",
        message: `Are you sure you want to duplicate ${$store.selectedQuestions.length} post(s)?`,
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
        accept: async () => {
            try {
                loading.value = true;
                const selectedQuestionsData = questions.filter((q) => $store.selectedQuestions.includes(q.id));
                duplicatingQuestions.value = selectedQuestionsData.map((question) => {
                    return {
                        id: question.id,
                        title: question.title,
                        status: "pending",
                    };
                });
                // open single modal for duplicate
                modalOperationType.value = "duplicate";
                modalVisible.value = true;
                await duplicateMultipleQuestions($store.selectedQuestions);
                toast.add({
                    severity: "success",
                    summary: "Duplication Started",
                    detail: `${$store.selectedQuestions.length} post(s) are being duplicated.`,
                    life: 5000,
                });
            } catch (e) {
                console.error(e);
            } finally {
                loading.value = false;
            }
        },
    });
};

const handleShareMultiple = () => {
    const selectedQuestionsData = questions.filter((q) => $store.selectedQuestions.includes(q.id));
    sharingQuestions.value = selectedQuestionsData.map((question) => {
        return {
            id: question.id,
            title: question.title,
            status: "pending",
        };
    });
    state.show.shareDialog = true;
    emit("startSharing", selectedQuestionsData);
};

// open modal after external share process initiates
const startSharingProcess = () => {
    modalOperationType.value = "share";
    modalVisible.value = true;
};

const handleArchiveMultiple = async () => {
    confirm.require({
        header: "Archive posts",
        message: `Are you sure you want to archive ${$store.selectedQuestions.length} post(s)?`,
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
        accept: async () => {
            try {
                loading.value = true;
                await deleteMultipleQuestions($store.selectedQuestions);
                toast.add({
                    severity: "success",
                    summary: "Archived",
                    detail: `${$store.selectedQuestions.length} post(s) archived successfully.`,
                    life: 3000,
                });
                if (params.filter.state === "created") {
                    emit("removeQuestions", $store.selectedQuestions);
                }
                emit("cancel");
            } catch (e) {
                console.error(e);
            } finally {
                loading.value = false;
            }
        },
    });
};

// Expose the function to parent component
defineExpose({
    startSharingProcess,
});

// ...existing code...
</script>

<template>
    <LaunchHeader title="Select posts" class="mb-4" @cancel="emit('cancel')">
        <template #header>
            <div class="ml-auto flex items-center gap-3" v-auto-animate>
                <Button
                    v-if="!!$store.selectedQuestions.length"
                    icon="prime:copy"
                    class="bg-white !px-2 !py-1 text-xs text-askr-green hover:brightness-90"
                    size="small"
                    :loading="loading"
                    @click="handleDuplicateMultiple"
                >
                    Duplicate
                </Button>
                <Button
                    v-if="!!$store.selectedQuestions.length"
                    icon="prime:share-alt"
                    class="bg-white !px-2 !py-1 text-xs text-askr-green hover:brightness-90"
                    size="small"
                    :loading="loading"
                    @click="handleShareMultiple"
                >
                    Share
                </Button>
                <Button
                    v-if="!!$store.selectedQuestions.length"
                    icon="prime:trash"
                    class="bg-white !px-2 !py-1 text-xs text-red-600 hover:brightness-90"
                    :loading="loading"
                    @click="handleArchiveMultiple"
                >
                    Archive
                </Button>
                <p v-if="!$store.selectedQuestions.length" class="py-0.5">Select posts</p>
            </div>
        </template>
    </LaunchHeader>

    <!-- single modal controlled by refs/computed -->
    <MassOperationProgressModal
        v-model:visible="modalVisible"
        :questions="modalQuestions"
        :operation-type="modalOperationType"
        @close="emit('cancel')"
    />
</template>

<style scoped></style>
